import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import UploadIcon from '@mui/icons-material/Upload';

interface ConfigurationUploadStatusProps {
  configurationId: string;
  uploadStatuses?: any[];
  isProcessing?: boolean;
  detailedStatus?: any;
  freshStatusData?: any;
}

export function ConfigurationUploadStatus({
  configurationId,
  uploadStatuses = [],
  isProcessing = false,
  detailedStatus,
  freshStatusData,
}: ConfigurationUploadStatusProps) {
  const staleLatestStatus = uploadStatuses.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0];

  const freshLatestStatus = freshStatusData?.latestStatus;
  const freshIsProcessing = freshStatusData?.isProcessing;

  const getActualStatus = () => {
    if (isProcessing || freshIsProcessing) {
      return {
        status: 'processing',
        source: 'processing-flag',
      };
    }

    if (detailedStatus && detailedStatus.status) {
      return {
        status: detailedStatus.status,
        source: 'detailed-status',
      };
    }

    if (freshLatestStatus) {
      return {
        status: freshLatestStatus.status,
        error: freshLatestStatus.error,
        source: 'fresh-data',
      };
    }

    if (staleLatestStatus) {
      return {
        status: staleLatestStatus.status,
        error: staleLatestStatus.error,
        source: 'stale-data',
      };
    }

    return {
      status: 'completed',
    };
  };

  const actualStatus = getActualStatus();

  if (!actualStatus) {
    return null;
  }

  const renderStatusIndicator = () => {
    switch (actualStatus.status) {
      case 'pending':
      case 'processing':
        const progress = detailedStatus?.progress || 0;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress
              size={16}
              variant={progress > 0 ? 'determinate' : 'indeterminate'}
              value={progress}
            />
            <Chip
              label={`Processing ${progress > 0 ? `${Math.round(progress)}%` : ''}`}
              color="warning"
              size="small"
              variant="outlined"
              icon={<UploadIcon />}
            />
          </Box>
        );

      case 'completed':
        return (
          <Tooltip title="Upload completed successfully">
            <Chip
              label="Ready"
              color="success"
              size="small"
              variant="outlined"
              icon={<CheckCircleIcon />}
            />
          </Tooltip>
        );

      case 'failed':
        return (
          <Tooltip
            title={`Upload failed: ${actualStatus.error || 'Unknown error'}`}
          >
            <Chip
              label="Failed"
              color="error"
              size="small"
              variant="outlined"
              icon={<ErrorIcon />}
            />
          </Tooltip>
        );

      default:
        return (
          <Tooltip title="Upload completed successfully">
            <Chip
              label="Ready"
              color="success"
              size="small"
              variant="outlined"
              icon={<CheckCircleIcon />}
            />
          </Tooltip>
        );
    }
  };

  const showProgressBar =
    (isProcessing || freshIsProcessing) && detailedStatus?.progress > 0;

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {renderStatusIndicator()}
      {showProgressBar && (
        <LinearProgress
          variant="determinate"
          value={detailedStatus.progress}
          sx={{ width: 100, height: 4 }}
        />
      )}
    </Box>
  );
}
